import { parse } from 'url';

import memoize from 'lodash/memoize';

import { createSocketIOCollabProvider } from '@atlaskit/collab-provider/socket-io-provider';
import type { Config as CollabProviderConfig } from '@atlaskit/collab-provider/types';
import type { PresenceActivity } from '@atlaskit/editor-common/collab';
import type { Provider } from '@atlaskit/collab-provider';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures';

import { COLLAB_EDIT_PROVIDER_MEASURES } from './CollabEditProviderMeasures';
import type { InitialDraft } from './fetchDraft';
import { getCollabProviderUrl } from './collabProviderUrl';
import { fetchNativeCollabAGGUsers } from './fetchNativeCollabAGGUsers';
import { fetchNativeCollabConfluenceUsers } from './fetchNativeCollabConfluenceUsers';
import type { FetchUsersClient } from './useFetchUsersClient';
import { fetchAnonymousAsset } from './fetchAnonymousAsset';

export const resolverOfMemoize = (options: CollabEditorProviderOptions) => {
	const cloudId = options?.cloudId ?? '';
	const contentId = options?.contentId ?? '';
	const contentType = options?.contentType ?? '';
	const fetchUsersClient = options?.fetchUsersClient ?? '';
	return `${cloudId}_${contentId}_${contentType}_${fetchUsersClient}`;
};

export type CollabEditorProviderOptions = {
	contentId: string;
	cloudId: string;
	contentType: string;
	permissionTokenRefresh?: CollabProviderConfig['permissionTokenRefresh'];
	initialDraft?: InitialDraft | null;
	isLivePage?: boolean;
	presenceId?: string;
	presenceActivity?: PresenceActivity;
	userId?: string | null;
	fetchUsersClient: FetchUsersClient;
	onUserFetchError?(err: unknown): void;
};

export const getCollabEditProvider = memoize((options: CollabEditorProviderOptions): Provider => {
	// start measuring duration for initializing the provider
	CollabEditProviderMeasures.markMeasureStart(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT,
	);
	CollabEditProviderMeasures.markMeasureStart(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_FETCH,
	);
	const {
		contentId,
		cloudId,
		contentType,
		permissionTokenRefresh,
		initialDraft,
		isLivePage,
		onUserFetchError,
		presenceId,
		presenceActivity,
		userId,
		fetchUsersClient,
	} = options;
	const url = getCollabProviderUrl();
	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
	const product = isEmbeddedConfluence ? 'embeddedConfluence' : 'confluence';
	const documentAri = `ari:cloud:confluence:${cloudId}:${contentType}/${contentId}`;
	const subProduct = isEmbeddedConfluence
		? parse(location.search, true).query.parentProduct
		: isLivePage
			? 'live'
			: undefined;

	const fetchAnonymousAssetCallback = async (id: string | undefined) => {
		try {
			return await fetchAnonymousAsset(id);
		} catch (error) {
			onUserFetchError?.(error);
		}

		return undefined;
	};

	// In order to support deduplication of the collaborator, presenceId will be set to the userId if it exists.
	// This means that anonymous users will still rely on presenceId and not get deduplicated.
	const collaboratorPresenceId = userId ?? presenceId;

	CollabEditProviderMeasures.markMeasureEnd(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_FETCH,
	);

	CollabEditProviderMeasures.markMeasureStart(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_CREATED,
	);

	// DO NOT add any asynchronous calls/behavior to the collab provider creation. This has caused incidents
	// in the past where the provider promise never resolves and editing breaks.
	// Context: https://hello.atlassian.net/wiki/spaces/~589311246/pages/2554442259/Investigate+async+issue+HOT-103180
	return createSocketIOCollabProvider({
		documentAri,
		url,
		batchProps: {
			getUsers:
				fetchUsersClient === 'agg' ? fetchNativeCollabAGGUsers : fetchNativeCollabConfluenceUsers,
			batchSize: 50,
			onError: onUserFetchError,
		},
		getAnalyticsWebClient: getAnalyticsWebClient(),
		permissionTokenRefresh,
		need404: true,
		productInfo: {
			product,
			subProduct: Array.isArray(subProduct) ? subProduct.join('-') : subProduct,
		},
		// either pass the initialDraft that was fetched on hover, or the draftResponse that was fetched on click
		initialDraft: initialDraft || undefined,
		isBufferingEnabled: Boolean(initialDraft), // buffering should only be enabled when the initial draft is present
		__livePage: isLivePage,
		presenceId: collaboratorPresenceId,
		presenceActivity,
		throwOnNotConnected: true,
		fetchAnonymousAsset: fetchAnonymousAssetCallback,
	});
}, resolverOfMemoize);
